import { useReduxState } from 'hooks';
import { Avatar, RichTextValue, TextWithLineBreaks } from 'components/common';
import { BoxRow } from 'components/common/box';
import {
  AchievementItemAddedBy,
  AchievementCompletionsFillTemplateActions,
  AchievementCompletionsFillTemplateAdd
} from '.';

import type { AchievementProgress } from 'shared-types';

interface Props {
  achievement?: {
    id: number;
    isOwnerOrSuperadmin?: boolean;
    isSupervisor?: boolean;
    userAdminPermissions?: string[];
  };
  adminMode?: boolean;
  canAnswer: boolean;
  item: AchievementProgress['template'][number];
  itemIndex: number;
  targetUserId: number;
  updateItems?: () => Promise<void>;
}

const AchievementCompletionsFillTemplateConversation = ({
  achievement,
  adminMode = false,
  canAnswer,
  item,
  itemIndex,
  targetUserId,
  updateItems
}: Props) => {
  const { messages, theme } = useReduxState();

  const answer = item.answers[0];

  return (
    <BoxRow>
      <div className="content">
        <div className="status">
          <Avatar size="24px" fontSize="1.4rem" padding="2px 0">
            {itemIndex}
          </Avatar>
        </div>

        <div className="item">
          <div className="item-inner">
            <div className="item-text">
              <RichTextValue html={item.question} />
            </div>

            {answer?.text ? (
              <div className="comment">
                <TextWithLineBreaks text={answer.text} />
              </div>
            ) : null}

            {answer?.user ? (
              <div className="added-by">
                <AchievementItemAddedBy
                  date={answer.date}
                  enableUserLink={adminMode}
                  user={answer.user}
                />
              </div>
            ) : canAnswer ? (
              <div className="add">
                <AchievementCompletionsFillTemplateAdd
                  achievementId={achievement?.id}
                  actionText={messages.commonSaveAnswer}
                  allowEmptyText={false}
                  buttonText={messages.commonSaveAnswer}
                  confirmText={messages.commonSaveAnswerConfirm}
                  confirmTitle={messages.commonSaveAnswer}
                  successMessage={messages.commonSaveAnswerSuccess}
                  targetUserId={targetUserId}
                  templateItemId={item.id}
                  textInfoMessage={
                    adminMode
                      ? messages.achievementUsersTemplateAnswerInfo
                      : undefined
                  }
                  textPlaceholder={messages.commonAddAnswer}
                  updateUserAchievement={updateItems}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className="actions">
          <AchievementCompletionsFillTemplateActions
            achievement={achievement}
            allowEmptyText={false}
            canEdit={canAnswer && item.isCompleted}
            deleteActionText={messages.commonDelete}
            deleteButtonText={messages.commonDelete}
            deleteSuccessMessage={messages.commonDeleteAnswerSuccess}
            deleteText={messages.commonDeleteAnswerConfirm}
            deleteTitle={messages.commonDeleteAnswer}
            editSuccessMessage={messages.commonSaveAnswerSuccess}
            editTextInfoMessage={
              adminMode
                ? messages.achievementUsersTemplateAnswerInfo
                : undefined
            }
            editTextLabel={messages.commonAnswer}
            editTitle={messages.commonEditAnswer}
            id={item.id}
            targetUserId={targetUserId}
            updateUserAchievement={updateItems}
            text={answer?.text}
          />
        </div>

        <style jsx>{`
          .content {
            align-items: flex-start;
            display: flex;
            padding: 5px 0;
          }
          .status {
            flex-grow: 0;
            flex-shrink: 0;
            margin-right: 15px;
          }
          .item {
            flex-grow: 1;
            flex-shrink: 1;
            margin-right: 30px;
            padding: 1px 0;
          }
          .item-inner {
            max-width: 720px;
          }
          .item-text {
            color: ${theme.textColorLight};
            font-size: 1.5rem;
            font-weight: 600;
            line-height: 1.4;
          }
          .comment {
            background-color: ${theme.lightGreyLighter};
            border-radius: 7px;
            color: ${theme.textColorLight};
            font-size: 1.4rem;
            font-weight: 400;
            margin-top: 10px;
            padding: 10px;
          }
          .added-by {
            margin-top: 10px;
          }
          .add {
            margin: 15px 0 5px;
          }
          .actions {
            flex-grow: 0;
            flex-shrink: 0;
          }
        `}</style>
      </div>
    </BoxRow>
  );
};

export default AchievementCompletionsFillTemplateConversation;
